import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { PopoverShell } from '../../../components/PopoverShell/PopoverShell';
import {
    formatItemMetadataSummary,
    getDisplayableItemMetadataEntries,
} from './itemMetadataModel';
import { ItemMetadataDetail } from './ItemMetadataDetail';
import styles from './ItemMetadataCapsule.module.css';

/**
 * Fixed-width top-bar metadata capsule; full values in a click popover.
 */
export function ItemMetadataCapsule({
    uiConfig,
    row,
    className,
}) {
    const entries = getDisplayableItemMetadataEntries(uiConfig, row);
    const [open, setOpen] = useState(false);
    const [truncated, setTruncated] = useState(false);
    const buttonRef = useRef(null);
    const [anchorRect, setAnchorRect] = useState(null);
    const reactId = useId();
    const titleId = `item-metadata-popover-${reactId}`;

    const summary = formatItemMetadataSummary(entries);
    const singleField = entries.length === 1;
    const popoverTitle = singleField
        ? (String(entries[0].label || entries[0].key || '').trim() || 'Details')
        : 'Item metadata';

    useLayoutEffect(() => {
        const el = buttonRef.current;
        if (!el) {
            setTruncated(false);
            return;
        }
        setTruncated(el.scrollWidth > el.clientWidth);
    }, [summary]);

    useEffect(() => {
        if (!open) return;
        const update = () => {
            const rect = buttonRef.current?.getBoundingClientRect?.();
            if (rect) setAnchorRect(rect);
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [open]);

    if (entries.length === 0) return null;

    return (
        <div className={[styles.wrap, className].filter(Boolean).join(' ')}>
            <button
                ref={buttonRef}
                type="button"
                className={[
                    styles.capsule,
                    truncated ? styles.capsuleTruncated : '',
                ].filter(Boolean).join(' ')}
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={summary}
                title={summary}
            >
                <span className={styles.capsuleText}>{summary}</span>
                {truncated ? (
                    <span className={styles.capsuleChevron} aria-hidden="true">
                        ›
                    </span>
                ) : null}
            </button>

            <PopoverShell
                open={open}
                strategy="fixed"
                anchorRect={anchorRect}
                placement="bottom-start"
                offset={8}
                pinned={false}
                onClose={() => setOpen(false)}
                titleId={titleId}
                bodyClassName={styles.popoverBody}
            >
                <div id={titleId} className={styles.popoverTitle}>
                    {popoverTitle}
                </div>
                <ItemMetadataDetail
                    entries={entries}
                    className={styles.popoverDetail}
                    showLabels={!singleField}
                />
            </PopoverShell>
        </div>
    );
}
